import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Plus, Edit2, X, Check, Loader2, Building2, CheckCircle2, PowerOff, Info, LocateFixed, ExternalLink, FileSpreadsheet, ChevronDown } from "lucide-react";
import { API, formatApiError } from "../auth/api";
import ImportDialog from "../components/ImportDialog";
import { CalStatus, FacilitiesSection, SiteCalendars, SiteMaterials } from "./ExternalCustomers";
import WeeklyHours, { weekSummary } from "../components/WeeklyHours";

/**
 * Lokationer for logistik admin (2026-10-16, se LOGISTICS_TIERS.md).
 *  - Premium logistikfirmaer: egne lokationer (depot, egen plads, omlastestation,
 *    værksted) som de selv opretter og vedligeholder - med deres containere.
 *    Kundernes steder (byggepladser, butikker) står på kunden (Kunder).
 *  - Genbrugsplads-kunders pladser vises read-only, og kun når firmaet er
 *    tilknyttet mindst én kunde.
 */

const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const input = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const label = "block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700";

const EMPTY = { name: "", address: "", city: "", lat: "", lng: "", weekly_hours: null };

function LocationForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, lat: initial?.lat ?? "", lng: initial?.lng ?? "" });
  const [saving, setSaving] = useState(false);
  const [gpsTouched, setGpsTouched] = useState(false);
  const [finding, setFinding] = useState(false);
  const [gpsNote, setGpsNote] = useState("");
  const addrChanged = (f.address || "") !== (initial?.address || "") || (f.city || "") !== (initial?.city || "");
  const findGps = async () => {
    const q = [f.address, f.city].map(x => (x || "").trim()).filter(Boolean).join(", ");
    if (!q) { toast.error("Skriv adresse og by først"); return; }
    setFinding(true);
    try {
      const { data } = await axios.get(`${API}/geocode`, { params: { q } });
      setF(x => ({ ...x, lat: Number(data.lat).toFixed(6), lng: Number(data.lng).toFixed(6) }));
      setGpsTouched(true);
      setGpsNote(data.source === "postnr" ? "Omtrentlig placering (byens midte) - ret gerne præcist" : (data.label || "Fundet"));
    } catch (e) { toast.error(formatApiError(e)); }
    setFinding(false);
  };
  const save = async () => {
    if (!f.name.trim()) { toast.error("Skriv et navn"); return; }
    setSaving(true);
    // Ændres adressen uden at GPS er rettet, sendes GPS tomt, så serveren
    // finder den nye placering automatisk.
    const resetGps = addrChanged && !gpsTouched;
    const body = {
      name: f.name.trim(), address: f.address.trim(), city: f.city.trim(), weekly_hours: f.weekly_hours || {},
      lat: resetGps || f.lat === "" ? null : Number(String(f.lat).replace(",", ".")),
      lng: resetGps || f.lng === "" ? null : Number(String(f.lng).replace(",", ".")),
    };
    try {
      if (initial?.id) await axios.put(`${API}/plads/${initial.id}`, body);
      else await axios.post(`${API}/plads`, body);
      toast.success(initial?.id ? "Lokationen er opdateret" : "Lokationen er oprettet");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="p-4 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1"><span className={label}>Navn</span>
          <input className={input} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Fx Depot Køge" autoFocus /></div>
        <div className="md:col-span-1"><span className={label}>Adresse</span>
          <input className={input} value={f.address} onChange={e => setF({ ...f, address: e.target.value })} placeholder="Vej og nr." /></div>
        <div><span className={label}>By</span>
          <input className={input} value={f.city} onChange={e => setF({ ...f, city: e.target.value })} /></div>
      </div>
      <div className="rounded-lg bg-white/70 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold flex items-center gap-1.5"><LocateFixed className="w-4 h-4 text-slate-400" /> GPS-placering</div>
          <div className="flex gap-2">
            <button type="button" className={btnGhost} onClick={findGps} disabled={finding}>{finding ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} Find fra adressen</button>
            {f.lat !== "" && f.lng !== "" && (
              <a className={btnGhost} href={`https://www.google.com/maps/search/?api=1&query=${String(f.lat).replace(",", ".")},${String(f.lng).replace(",", ".")}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4" /> Vis på kort</a>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Bruges som startpunkt, når chaufføren trykker "Optimer rute": turene sorteres efter afstand fra lokationen, og der vises km pr. tur.
          Lader du felterne stå tomme, finder systemet placeringen automatisk ud fra adressen, når du gemmer.
          Skriv kun tal selv, hvis stedet ikke har en rigtig adresse (fx en byggeplads) - kopiér dem fra Google Maps (højreklik på stedet).
        </p>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div><span className={label}>Breddegrad</span>
            <input className={input} inputMode="decimal" value={f.lat} onChange={e => { setGpsTouched(true); setF({ ...f, lat: e.target.value }); }} placeholder="Fx 55.4565" /></div>
          <div><span className={label}>Længdegrad</span>
            <input className={input} inputMode="decimal" value={f.lng} onChange={e => { setGpsTouched(true); setF({ ...f, lng: e.target.value }); }} placeholder="Fx 12.1820" /></div>
        </div>
        {gpsNote && <p className="text-[11px] text-emerald-700 dark:text-emerald-400">{gpsNote}</p>}
        {addrChanged && !gpsTouched && initial?.id && f.lat !== "" && <p className="text-[11px] text-amber-700">Adressen er ændret - GPS findes igen automatisk, når du gemmer.</p>}
      </div>
      {/* 2026-10-27: Lokationens egne åbningstider (fx depotets) - Ny tur viser dagens tider. Pr. materiale kun, hvis der står containere med fraktioner. */}
      <WeeklyHours value={f.weekly_hours} onChange={(w) => setF({ ...f, weekly_hours: w })} testid="location-week" />
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}><X className="w-4 h-4" /> Annuller</button>
        <button className={btnPrimary} disabled={saving} onClick={save} data-testid="location-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem</button>
      </div>
    </div>
  );
}

function LocationRow({ p, badge, actions, children }) {
  return (
    <div className="border-t border-slate-100 dark:border-slate-700 first:border-t-0">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="font-semibold">{p.name}</span>
          {p.location_code && <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-mono">{p.location_code}</span>}
          {p.lat && p.lng
            ? <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">GPS</span>
            : <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Ingen GPS</span>}
          {badge}
        </div>
        {(p.address || p.city) && <div className="text-xs text-slate-500 ml-6 mt-0.5">{[p.address, p.city].filter(Boolean).join(", ")}</div>}
        {p.weekly_hours && <div className="text-xs text-slate-500 ml-6 mt-0.5">Åbent: {weekSummary(p.weekly_hours)}</div>}
      </div>
      {actions}
    </div>
    {children}
    </div>
  );
}

export default function LogisticsLocations({ profile, pladsList = [], onChanged }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmOff, setConfirmOff] = useState(null);
  const [importing, setImporting] = useState(false);   // 2026-10-25: lokationer fra Excel
  // 2026-10-27: Leveringsadresser og åbningstider pr. materiale på en egen lokation.
  const [calOpen, setCalOpen] = useState(null);
  const [canDelivery, setCanDelivery] = useState(false);
  const [calStatus, setCalStatus] = useState({});
  // 2026-10-28: Materialer og containere på egne lokationer (fx depot eller omlastestation) - chaufførens
  // "Opret tur" og Ny tur vælger containeren fra listen.
  const [matSugg, setMatSugg] = useState([]);
  useEffect(() => {
    axios.get(`${API}/logistics/delivery-access`).then(r => setCanDelivery(!!r.data?.can_edit)).catch(() => setCanDelivery(false));
    axios.get(`${API}/logistics/material-suggestions`).then(r => setMatSugg(r.data?.materials || [])).catch(() => setMatSugg([]));
  }, []);
  useEffect(() => {
    if (canDelivery) axios.get(`${API}/logistics/calendar-status`).then(r => setCalStatus(r.data || {})).catch(() => setCalStatus({}));
  }, [canDelivery, calOpen]);
  const ownId = profile?.company_id;
  const isPremium = !!profile?.is_premium;
  const own = pladsList.filter(p => ownId && p.company_id === ownId);
  // 2026-10-26: Eksterne kunders pladser vedligeholder I selv - de står under Eksterne kunder.
  const customerSites = pladsList.filter(p => (!ownId || p.company_id !== ownId) && !p.customer_external);
  const customerName = (cid) => (profile?.connected_customers || []).find(c => c.id === cid)?.name;

  const deactivate = async (p) => {
    try { await axios.delete(`${API}/plads/${p.id}`); toast.success(`${p.name} er deaktiveret`); setConfirmOff(null); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="logistics-locations">
      {/* 2026-10-26: Hvad er hvad - lokation, leveringsadresse og kundens adresser. */}
      <details className={`${card} px-4 py-3 text-sm`} data-testid="locations-definitions">
        <summary className="font-semibold cursor-pointer flex items-center gap-2"><Info className="w-4 h-4 text-sky-600" /> Lokation, leveringsadresse og kundens adresser - hvad er forskellen?</summary>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-slate-600 dark:text-slate-300">
          <div className="rounded-xl bg-slate-50 dark:bg-slate-900 p-3"><div className="font-bold text-slate-800 dark:text-slate-100 mb-1">Lokation</div>
            Stedet, turen hører til: kundens genbrugsplads, en egen kundes plads - eller jeres egen lokation (depot, egen plads), som er jeres base.
            En egen tur til en kundes byggeplads eller butik hører til jeres lokation; kundens adresse er dér, der hentes eller afleveres.
            Bruges til at vælge sted på turen, priser pr. lokation, rapporter og ruten.</div>
          <div className="rounded-xl bg-slate-50 dark:bg-slate-900 p-3"><div className="font-bold text-slate-800 dark:text-slate-100 mb-1">Leveringsadresse (modtageanlæg)</div>
            Hvor læsset køres hen, fx Geminor Køge Havn. Sættes i lokationens åbnings- og helligdagskalender pr. materiale: én primær pr. dag og evt. alternativer med egne tider, der kan vælges ved bestilling og af chaufføren (er den primære lukket, bruges det første åbne alternativ). Bruges til chaufførens adresse, pris pr. leveringsadresse og rapporter.</div>
          <div className="rounded-xl bg-slate-50 dark:bg-slate-900 p-3"><div className="font-bold text-slate-800 dark:text-slate-100 mb-1">Kundens adresser</div>
            Byggepladser, butikker, lagre o.l. hos jeres egne kunder (ikke genbrugspladser). Gemmes på kunden (Kunder › Egne kunder - også fra Excel) og vælges under afhentning og aflevering i Ny tur.</div>
        </div>
        <p className="mt-3 text-xs text-slate-500">Hvem retter dem? En kunde, der selv bruger ContainerFlow, vedligeholder sine pladser, leveringsadresser og åbningstider - de kommer automatisk med på turen, og I kan vælge et alternativ eller skrive en anden adresse på den enkelte bestilling. Jeres egne kunder med pladser og jeres egne lokationer vedligeholder I ("Leveringsadresser og åbningstider"), og leveringsadresserne vælges fra jeres liste over modtageanlæg.</p>
      </details>
      {isPremium && (
        <section className={card}>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-600" /> Mine lokationer</h2>
              <p className="text-xs text-slate-500">Jeres egne steder: depot, egen plads, værksted. Bruges når du opretter egne ture - chaufførerne finder dem under "Egne ture". Kundernes byggepladser og lagre gemmes på kunden.</p>
            </div>
            {!creating && <div className="flex flex-wrap gap-2">
              <button className={btnGhost} onClick={() => setImporting(true)} data-testid="locations-import"><FileSpreadsheet className="w-4 h-4" /> Importér fra Excel</button>
              <button className={btnPrimary} onClick={() => { setCreating(true); setEditing(null); }}><Plus className="w-4 h-4" /> Ny lokation</button>
            </div>}
          </div>
          {importing && <ImportDialog kind="locations" title="Importér lokationer" onClose={() => setImporting(false)} onDone={onChanged} />}
          {creating && <div className="p-4"><LocationForm onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); onChanged(); }} /></div>}
          {own.length === 0 && !creating && (
            <div className="p-8 text-center text-sm text-slate-500">Ingen lokationer endnu. Opret din første lokation for at kunne oprette egne ture.</div>
          )}
          {own.map(p => editing === p.id ? (
            <div key={p.id} className="p-4 border-t border-slate-100 dark:border-slate-700">
              <LocationForm initial={p} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
            </div>
          ) : (
            <LocationRow key={p.id} p={p} actions={
              <div className="flex items-center gap-1 shrink-0">
                {canDelivery && p.location_code && <CalStatus mats={calStatus[p.location_code] || []} />}
                {canDelivery && p.location_code && (
                  <button className={btnGhost} onClick={() => setCalOpen(calOpen === p.id ? null : p.id)} data-testid={`own-calendars-${p.location_code}`}
                    title="Kun hvis der står containere med fraktioner på lokationen (fx en omlastestation) - lokationens egne åbningstider sættes under Rediger">
                    Containere: leveringsplan pr. materiale <ChevronDown className={`w-4 h-4 transition-transform ${calOpen === p.id ? "rotate-180" : ""}`} /></button>
                )}
                <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" title="Rediger" onClick={() => { setEditing(p.id); setCreating(false); }}><Edit2 className="w-4 h-4" /></button>
                {confirmOff === p.id ? (
                  <>
                    <button className="px-2 py-1 rounded-lg bg-red-600 text-white text-xs font-bold" onClick={() => deactivate(p)}>Deaktivér</button>
                    <button className="px-2 py-1 rounded-lg text-xs" onClick={() => setConfirmOff(null)}>Nej</button>
                  </>
                ) : (
                  <button className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600" title="Deaktivér (ture og historik bevares)" onClick={() => setConfirmOff(p.id)}><PowerOff className="w-4 h-4" /></button>
                )}
              </div>
            }>
              {p.location_code && <div className="px-4 pb-3 -mt-1"><SiteMaterials base={`${API}/logistics/own-locations/${p.id}`} canEdit site={{ id: p.id, name: p.name, location_code: p.location_code }}
                materialSuggestions={matSugg} /></div>}
              {calOpen === p.id && <div className="px-4 pb-4 space-y-2"><p className="text-xs text-slate-500" data-testid="own-calendars-hint">Kun til containere med fraktioner på lokationen (fx en omlastestation, hvor pap køres til ét anlæg og metal til et andet). Lokationens egne åbningstider (fx depotets) sættes under Rediger - de gælder alle ture, også fragt.</p><SiteCalendars code={p.location_code} free excel={{ kind: "calendars" }}
                siblings={own.filter(x => x.location_code && x.id !== p.id).map(x => ({ code: x.location_code, name: x.name }))} /></div>}
            </LocationRow>
          ))}
        </section>
      )}

      {/* 2026-10-28: Kunderne (egne, med pladser og i ContainerFlow) står under Kunder - her jeres egne steder. */}
      {isPremium && <FacilitiesSection />}

      {!isPremium && (
        <section className={card}>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <h2 className="font-bold text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-600" /> {isPremium ? "Kunders genbrugspladser" : "Genbrugspladser"}</h2>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Auto-synkroniseret fra kunder</span>
          </div>
          <div className="mx-4 my-3 flex gap-2 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-xs text-sky-900 dark:text-sky-100">
            <Info className="w-4 h-4 shrink-0" />
            Når en kunde tildeler jer som logistik-partner på en lokation, vises pladsen her automatisk. Kunden vedligeholder pladserne.
          </div>
          {customerSites.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Ingen pladser endnu.</div>
          ) : customerSites.map(p => (
            <LocationRow key={p.id} p={p} badge={
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                Kunde: {customerName(p.company_id) || "Anden kunde"}
              </span>
            } />
          ))}
        </section>
      )}
    </div>
  );
}
